// We use fetch to call our serverless function (no OpenAI client in the browser).
// The new suggestTags backend is KNN+cooc over the user's embedded thoughts and
// requires the user's Supabase JWT in the Authorization header so RLS scopes
// the query — see docs/plans/2026-05-25-002-feat-knn-tag-suggester-plan.md.
import { supabase } from '../supabase';

async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? `Bearer ${session.access_token}` : null;
}

// Hybrid tag suggestions: fuse KNN (your history) + LLM (fresh tags), agreed
// tags first, full union. ON by default; toggle OFF live in the browser console
// with `localStorage.setItem('hybridTags','off')` to A/B against pure KNN.
export function hybridTagsEnabled() {
  try { return localStorage.getItem('hybridTags') !== 'off'; } catch { return true; }
}

/**
 * Reflect: given a free-form message ("how I'm feeling"), return the user's own
 * most relevant saved thoughts. The server holds the whole corpus in context and
 * returns real saved rows (never a fabricated quote).
 *
 * @param {string} message - the free-form entry to reflect on
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<Array<{id: string, ts: string, body: string, importance: number}>>}
 */
export const reflectThoughts = async (message, options = {}) => {
  const { signal } = options;
  try {
    if (!message || !message.trim()) return [];

    const authHeader = await getAuthHeader();
    if (!authHeader) {
      console.error('No active session for reflectThoughts');
      return [];
    }

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({ type: 'reflectThoughts', message }),
      signal,
    });

    const data = await response.json();
    if (!response.ok) {
      // Log the status so a 429 (rate limit) is distinguishable from a genuine
      // empty result in the console.
      console.error('API Error:', response.status, data);
      return [];
    }
    return Array.isArray(data.thoughts) ? data.thoughts : [];
  } catch (error) {
    // Propagate aborts so callers can distinguish cancellation from failure.
    if (error?.name === 'AbortError') throw error;
    console.error('Error reflecting thoughts:', error);
    return [];
  }
};

/**
 * Analyzes a thought and suggests relevant tags based on the thought content.
 *
 * Backed by KNN+cooc retrieval over the user's own embedded thoughts (no LLM
 * at suggestion time). The availableTags and existingTags params are accepted
 * for backward-compat with the previous LLM-based implementation but are
 * ignored — the server queries the user's tagged thoughts directly via RLS.
 *
 * @param {string} thoughtText - The content of the thought to analyze
 * @param {Array<string>} availableTags - (ignored, kept for compat)
 * @param {Array<string>} existingTags - (ignored, kept for compat)
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<Array<string>>} - Promise resolving to an array of suggested tags
 */
// availableTags/existingTags are kept for backward-compat with the old LLM
// signature but ignored by the KNN backend (it queries the user's own tags).
// eslint-disable-next-line no-unused-vars
export const suggestTagsForThought = async (thoughtText, availableTags, existingTags = [], options = {}) => {
  const { signal, hybrid } = options;
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return [];
    }

    const authHeader = await getAuthHeader();
    if (!authHeader) {
      console.error('No active session for suggestTags');
      return [];
    }

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        // Caller may pass an explicit hybrid snapshot so the request, the cache
        // key, and the post-response filter all agree on one value; fall back to
        // the live flag when omitted.
        type: 'suggestTags',
        hybrid: hybrid ?? hybridTagsEnabled(),
        thoughtText,
      }),
      signal,
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error('API Error:', responseData);
      return [];
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return [];
    }

    // The KNN backend returns choices[0].message.content as a clean
    // JSON-stringified array (e.g. '["focus","work"]'). Parse it directly.
    //
    // NOTE: the old LLM-era sanitizer (backtick/markdown stripping, and a
    // `.replace(/'/g, '"')`) used to run here — it was for messy LLM prose
    // output, but it CORRUPTED any tag containing an apostrophe ("don't" ->
    // broken JSON). The KNN output is already clean, so the sanitizer is gone.
    const suggestedTagsText = responseData.choices[0].message.content.trim();
    try {
      const suggestedTags = JSON.parse(suggestedTagsText);
      return Array.isArray(suggestedTags) ? suggestedTags : [];
    } catch (error) {
      console.error('Failed to parse tag suggestions as JSON:', error, suggestedTagsText);
      return [];
    }
  } catch (error) {
    // Propagate aborts so the cache evicts the entry (otherwise an
    // aborted fetch resolves as `[]` and is cached as a successful
    // empty result — every retry then short-circuits forever).
    if (error?.name === 'AbortError') throw error;
    console.error('Error suggesting tags:', error);
    return [];
  }
};

/**
 * Suggests new conceptual themes for tagging, focusing on new tag ideas
 * not already in the user's tagging system
 * 
 * @param {string} thoughtText - The content of the thought to analyze
 * @param {Array<string>} availableTags - List of all available tags in the system
 * @param {Array<string>} existingTags - Tags already associated with this thought (optional)
 * @returns {Promise<Array<string>>} - Promise resolving to an array of suggested new themes
 */
export const suggestNewThemes = async (thoughtText, availableTags, existingTags = [], options = {}) => {
  const { signal } = options;
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return [];
    }

    if (!availableTags || !Array.isArray(availableTags)) {
      console.error('availableTags must be an array');
      return [];
    }

    // Ensure existingTags is an array
    const safeExistingTags = Array.isArray(existingTags) ? existingTags : [];

    console.log('Calling OpenAI API for new themes with:', {
      type: 'suggestNewThemes',
      thoughtText: thoughtText.substring(0, 50) + '...',
      availableTags: availableTags.length,
      existingTags: safeExistingTags.length
    });

    const authHeader = await getAuthHeader();
    if (!authHeader) {
      console.error('No active session for suggestNewThemes');
      return [];
    }

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        type: 'suggestNewThemes',
        thoughtText,
        availableTags,
        existingTags: safeExistingTags,
        model: 'gpt-4o-mini',
        temperature: 0.5,
      }),
      signal,
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return [];
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return [];
    }
    
    const suggestedTagsText = responseData.choices[0].message.content.trim();
    
    try {
      // Preprocess the response to fix common JSON parsing issues
      let processedResponse = suggestedTagsText
        // Replace backticks with double quotes
        .replace(/`/g, '"')
        // Replace single quotes with double quotes
        .replace(/'/g, '"')
        // Extract JSON array if it's wrapped in markdown code blocks
        .replace(/```json\s*([\s\S]*)\s*```/g, '$1')
        .replace(/```\s*([\s\S]*)\s*```/g, '$1')
        .trim();
      
      // Make sure the response starts with [ and ends with ]
      if (!processedResponse.startsWith('[')) {
        const startBracket = processedResponse.indexOf('[');
        if (startBracket >= 0) {
          processedResponse = processedResponse.substring(startBracket);
        }
      }
      if (!processedResponse.endsWith(']')) {
        const endBracket = processedResponse.lastIndexOf(']');
        if (endBracket >= 0) {
          processedResponse = processedResponse.substring(0, endBracket + 1);
        }
      }

      // Try to parse the response as JSON
      const suggestedTags = JSON.parse(processedResponse);
      return Array.isArray(suggestedTags) ? suggestedTags : [];
    } catch (error) {
      console.error('Failed to parse API response as JSON:', error);
      // If JSON parsing fails, try to extract tags using regex
      const matches = suggestedTagsText.match(/["'`]([^"'`]+)["'`]/g);
      return matches ? matches.map(m => m.replace(/["'`]/g, '')) : [];
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('Error suggesting new themes:', error);
    return [];
  }
};

/**
 * Generates an AI summary of thoughts over a given timeframe
 * 
 * @param {Array<Object>} thoughts - Array of thought objects to analyze
 * @param {number} timeframe - The timeframe in days that the thoughts cover
 * @returns {Promise<string>} - Promise resolving to a summary of insights
 */
export const generateThoughtSummary = async (thoughts, timeframe) => {
  try {
    if (!thoughts || !Array.isArray(thoughts) || thoughts.length === 0) {
      console.log('No thoughts provided for summary generation');
      return null;
    }

    // Extract only the thought content and ensure no null/undefined values
    const thoughtTexts = thoughts
      .filter(thought => thought && thought.thought && typeof thought.thought === 'string')
      .map(thought => thought.thought.trim())
      .filter(text => text !== '');
    
    // Return early if no valid thought texts remain
    if (thoughtTexts.length === 0) {
      console.log('No valid thought texts found for summary generation');
      return null;
    }

    console.log(`Generating summary for ${thoughtTexts.length} thoughts over ${timeframe} days`);
    console.log(thoughtTexts[0]);

    const authHeader = await getAuthHeader();
    if (!authHeader) {
      console.error('No active session for generateThoughtSummary');
      return null;
    }

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        type: 'generateSummary',
        thoughtTexts,
        timeframe,
        model: 'gpt-4o-mini',
        temperature: 0.7,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return null;
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return null;
    }
    
    return responseData.choices[0].message.content.trim();
  } catch (error) {
    console.error('Error generating thought summary:', error);
    return null;
  }
};

/**
 * Performs a deep psychological analysis of a thought
 * 
 * @param {string} thoughtText - The content of the thought to analyze
 * @returns {Promise<string>} - Promise resolving to the analysis and transcendence guidance
 */
export const getDeepAnalysis = async (thoughtText) => {
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return null;
    }

    const authHeader = await getAuthHeader();
    if (!authHeader) {
      console.error('No active session for getDeepAnalysis');
      return null;
    }

    const response = await fetch('/.netlify/functions/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        type: 'custom',
        prompt: `As a psychologist, analyze this thought deeply:

"${thoughtText}"

Provide a concise response with these sections:
1. Core Insight: Key psychological patterns and dynamics
2. Current Stage: Where this indicates you are in your journey
3. Next Step: What is the next step of transcendence for this thought and mindset?

Do not use markdown, just write plain text.

Keep each section to 4-5 sentences.`,
        model: 'gpt-4o-mini',
        temperature: 1,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return null;
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return null;
    }
    
    return responseData.choices[0].message.content.trim();
  } catch (error) {
    console.error('Error getting deep analysis:', error);
    return null;
  }
};

/**
 * Fire-and-forget hook: after a thought is inserted, queue an embedding write
 * so the next suggestion sees this thought as a possible neighbor. Does NOT
 * await the response — save UX must not wait on OpenAI. If the call fails,
 * the row stays with embedding NULL and the periodic backfill script
 * (scripts/backfill-embeddings.mjs) picks it up on next run.
 *
 * Uses fetch keepalive: true so the request survives tab navigation/unload —
 * without this, the browser cancels in-flight fetches when the user closes
 * the tab right after saving, silently leaving the row unembedded.
 *
 * @param {string} thoughtId
 * @param {string} body
 */
export const embedThought = async (thoughtId, body) => {
  try {
    if (!thoughtId || !body || body.trim().length < 10) return;
    const authHeader = await getAuthHeader();
    if (!authHeader) return;
    // Intentionally not awaited — fire and forget.
    // keepalive: true keeps the request alive across tab close / navigation.
    // (Browsers cap keepalive request body at 64KB; 32KB-capped body fits.)
    fetch('/api/openai', {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        type: 'embedThought',
        thoughtId,
        body,
      }),
    }).catch(e => {
      // Backfill catches it on the next run; not user-facing.
      console.error('Background embed failed (will retry via backfill):', e);
    });
  } catch (e) {
    console.error('embedThought wrapper failed:', e);
  }
}; 